// Страница управления типами упражнений для преподавателей: CRUD типов, привязка к разделам.

import { useEffect, useState } from 'react';
import {
  Container,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Row,
  Col,
  Card,
} from 'react-bootstrap';
import { BookFill, FolderFill, ChevronRight, Grid3x2GapFill, ListUl } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import useExerciseStore from '../../store/exerciseStore';
import AppNavbar from '../../components/common/AppNavbar';
import { PARSERS, FORM_TYPES } from '../../constants/exerciseParsers';

const INITIAL_FORM = { name: '', description: '', parser: 'keyword_transform', form_type: 'keyword_transform' };

// ── Type Card ─────────────────────────────────────────────────────────────────

function TypeCard({ type, exerciseCount, onClick }) {
  return (
    <Card
      onClick={onClick}
      style={{
        background: '#ffffff',
        border: '1px solid #e8eaf0',
        borderRadius: 'var(--ewl-radius-card)',
        boxShadow: 'var(--ewl-shadow-sm)',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        position: 'relative',
        minHeight: '160px',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = 'var(--ewl-shadow-md)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'var(--ewl-shadow-sm)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <Card.Body className="p-4">
        <div
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            color: '#adb5bd',
          }}
        >
          <ChevronRight size={16} />
        </div>

        <div className="mb-3">
          <FolderFill size={32} color="#4f6ef7" />
        </div>

        <div className="fw-bold mb-1" style={{ fontSize: '1rem', color: '#1e2a4a' }}>
          {type.name}
        </div>

        {type.description && (
          <div
            className="text-muted mb-2"
            style={{
              fontSize: '0.8125rem',
              lineHeight: '1.5',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {type.description}
          </div>
        )}

        <div className="text-muted" style={{ fontSize: '0.8125rem' }}>
          {exerciseCount}{' '}
          {exerciseCount === 1
            ? 'упражнение'
            : exerciseCount >= 2 && exerciseCount <= 4
            ? 'упражнения'
            : 'упражнений'}
        </div>
      </Card.Body>
    </Card>
  );
}

// ── Type List Item ─────────────────────────────────────────────────────────────

function TypeListItem({ type, exerciseCount, onClick }) {
  return (
    <Card
      className="border-0"
      style={{
        background: '#fff',
        border: '1px solid #e8eaf0',
        borderRadius: '0.625rem',
        boxShadow: 'var(--ewl-shadow-sm)',
        cursor: 'pointer',
        transition: 'box-shadow 0.15s',
      }}
      onClick={onClick}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = 'var(--ewl-shadow-md)')}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'var(--ewl-shadow-sm)')}
    >
      <Card.Body className="py-2 px-3">
        <div className="d-flex align-items-center gap-3">
          <FolderFill size={20} color="#4f6ef7" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <span className="fw-semibold" style={{ fontSize: '0.9rem', color: '#1e2a4a' }}>
              {type.name}
            </span>
            {type.description && (
              <span className="text-muted ms-2" style={{ fontSize: '0.8rem' }}>
                {type.description.slice(0, 60)}
                {type.description.length > 60 ? '\u2026' : ''}
              </span>
            )}
          </div>
          <span className="text-muted" style={{ fontSize: '0.8rem', flexShrink: 0 }}>
            {exerciseCount}{' '}
            {exerciseCount === 1
              ? 'упражнение'
              : exerciseCount >= 2 && exerciseCount <= 4
              ? 'упражнения'
              : 'упражнений'}
          </span>
          <ChevronRight size={14} color="#adb5bd" style={{ flexShrink: 0 }} />
        </div>
      </Card.Body>
    </Card>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function ExerciseTypesPage() {
  const navigate = useNavigate();
  const { types, exercises, fetchTypes, fetchExercises, createExerciseType } =
    useExerciseStore();

  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [modalShow, setModalShow] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([fetchTypes(), fetchExercises()]).finally(() => setLoading(false));
  }, []);

  const openCreate = (e) => {
    e.stopPropagation();
    setForm(INITIAL_FORM);
    setError('');
    setModalShow(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Введите название');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await createExerciseType(form);
      setModalShow(false);
    } catch (err) {
      setError(err?.response?.data?.detail ?? 'Ошибка при сохранении');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <AppNavbar />

      <div style={{ background: 'var(--ewl-bg-body)', minHeight: 'calc(100vh - 56px)' }}>
        <Container className="py-4">

          {/* ── App header ── */}
          <div className="d-flex align-items-center gap-3 mb-4">
            <BookFill size={28} color="#4f6ef7" />
            <div>
              <div className="fw-bold" style={{ fontSize: '1.05rem', color: '#1e2a4a', lineHeight: 1.2 }}>
                English Learning Platform
              </div>
              <div className="text-muted" style={{ fontSize: '0.8125rem' }}>
                Панель управления учителя
              </div>
            </div>
          </div>

          {/* ── Section header ── */}
          <div
            className="d-flex justify-content-between align-items-center mb-4 pb-3"
            style={{ borderBottom: '1px solid #e9ecef' }}
          >
            <div>
              <h5 className="fw-bold mb-0" style={{ color: '#1e2a4a' }}>
                Типы упражнений
              </h5>
              <div className="text-muted mt-1" style={{ fontSize: '0.875rem' }}>
                Выберите тип упражнения для управления
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div className="d-flex gap-1">
                <Button
                  size="sm"
                  variant={viewMode === 'grid' ? 'dark' : 'outline-secondary'}
                  onClick={() => setViewMode('grid')}
                >
                  <Grid3x2GapFill size={15} />
                </Button>
                <Button
                  size="sm"
                  variant={viewMode === 'list' ? 'dark' : 'outline-secondary'}
                  onClick={() => setViewMode('list')}
                >
                  <ListUl size={15} />
                </Button>
              </div>
              <Button variant="dark" onClick={openCreate} style={{ borderRadius: '0.5rem' }}>
                + Создать тип
              </Button>
            </div>
          </div>

          {/* ── Content ── */}
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : types.length === 0 ? (
            <Card
              className="text-center py-5"
              style={{
                background: '#fff',
                border: '1px solid #e8eaf0',
                borderRadius: 'var(--ewl-radius-card)',
                boxShadow: 'var(--ewl-shadow-sm)',
              }}
            >
              <Card.Body>
                <FolderFill size={40} color="#dee2e6" className="mb-3" />
                <h6 className="fw-semibold text-muted mb-1">Типов упражнений пока нет</h6>
                <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                  Создайте первый тип, чтобы начать добавлять упражнения
                </p>
                <Button variant="dark" size="sm" onClick={openCreate}>
                  + Создать тип
                </Button>
              </Card.Body>
            </Card>
          ) : viewMode === 'grid' ? (
            <Row xs={1} md={2} lg={3} className="g-3">
              {types.map((type) => {
                const count = exercises.filter((ex) => ex.type_id === type.id).length;
                return (
                  <Col key={type.id}>
                    <TypeCard
                      type={type}
                      exerciseCount={count}
                      onClick={() => navigate(`/teacher/exercise-types/${type.id}`)}
                    />
                  </Col>
                );
              })}
            </Row>
          ) : (
            <div className="d-flex flex-column gap-2">
              {types.map((type) => {
                const count = exercises.filter((ex) => ex.type_id === type.id).length;
                return (
                  <TypeListItem
                    key={type.id}
                    type={type}
                    exerciseCount={count}
                    onClick={() => navigate(`/teacher/exercise-types/${type.id}`)}
                  />
                );
              })}
            </div>
          )}
        </Container>
      </div>

      {/* ── Create Modal ── */}
      <Modal show={modalShow} onHide={() => setModalShow(false)}>
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600 }}>
              Новый тип упражнения
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {error && (
              <Alert variant="danger" className="py-2 px-3 mb-3" style={{ fontSize: '0.875rem' }}>
                {error}
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>
                Название *
              </Form.Label>
              <Form.Control
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="Грамматическое преобразование"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>
                Описание / инструкция
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Преобразуйте слово так, чтобы оно грамматически соответствовало..."
              />
            </Form.Group>

            {/* parser is hidden, default "keyword_transform" */}

            <Form.Group className="mb-3">
              <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>
                Парсер
              </Form.Label>
              <Form.Select
                value={form.parser}
                onChange={(e) => setForm((p) => ({ ...p, parser: e.target.value }))}
              >
                {PARSERS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>
                Тип формы
              </Form.Label>
              <Form.Select
                value={form.form_type}
                onChange={(e) => setForm((p) => ({ ...p, form_type: e.target.value }))}
              >
                {FORM_TYPES.map((ft) => (
                  <option key={ft.value} value={ft.value}>{ft.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setModalShow(false)}>
              Отмена
            </Button>
            <Button
              type="submit"
              variant="dark"
              disabled={saving}
              style={{ fontWeight: 500 }}
            >
              {saving ? <Spinner size="sm" animation="border" /> : 'Сохранить'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}
